import { ownEntries } from '#library/primitive.library.js';
import { isSymbol } from '#library/assertion.library.js';
import { Token } from '#tempo/support/support.symbol.js';
import { resolveLayoutOrderPure } from './engine.resolver.js';
const TOKEN_ALIAS = new Map(ownEntries(Token, true).map(([name, key]) => [key, name]));
// Support alias-driven ordering regardless of symbol identity by resolving
// token names (e.g. dt, wkd, tm) to their symbol descriptions.
const TOKEN_DESCRIPTION_BY_NAME = new Map(ownEntries(Token, true)
    .map(([name, key]) => [name, key.description ?? ''])
    .filter(([, description]) => description.length > 0));
export const DEFAULT_LAYOUT_CLASS = Symbol('default');
/**
 * Build the minimum controller map from the current layout order.
 * This is the baseline framework entry until additional classifications are added.
 */
export function createLayoutController(layout) {
    return {
        [DEFAULT_LAYOUT_CLASS]: getLayoutOrder(layout),
    };
}
/**
 * Reorder layouts according to a classification entry.
 * Unknown layout names are appended in their original relative order.
 *
 * @remarks
 * Must return the original layout reference when no reordering occurs (no-op paths).
 * This preserves referential equality for consumers expecting `resolvedLayout === originalLayout`
 * when the classification is missing or causes no changes.
 */
export function resolveLayoutClassificationOrder(layout, controller, classification = DEFAULT_LAYOUT_CLASS) {
    const preferred = controller[classification] ?? [];
    if (preferred.length === 0)
        return layout;
    const entries = ownEntries(layout);
    const lookup = new Map();
    entries.forEach(([key, value]) => {
        lookup.set(key, [key, value]);
        const description = key.description ?? '';
        if (description)
            lookup.set(description, [key, value]);
        const alias = TOKEN_ALIAS.get(key);
        if (alias)
            lookup.set(alias, [key, value]);
    });
    const next = [];
    const seen = new Set();
    preferred.forEach(name => {
        const isSym = isSymbol(name);
        const description = isSym ? (name.description ?? '') : '';
        const alias = isSym ? TOKEN_ALIAS.get(name) : undefined;
        const resolvedName = !isSym ? (TOKEN_DESCRIPTION_BY_NAME.get(name) ?? name) : undefined;
        const entry = isSym
            ? (lookup.get(name) ?? lookup.get(description) ?? (alias ? lookup.get(alias) : undefined))
            : (lookup.get(resolvedName) ?? lookup.get(name));
        if (!entry)
            return;
        if (seen.has(entry[0]))
            return;
        seen.add(entry[0]);
        next.push(entry);
    });
    entries.forEach(entry => {
        if (!seen.has(entry[0]))
            next.push(entry);
    });
    const changed = next.length === entries.length && next.some((entry, idx) => entry[0] !== entries[idx][0]);
    return changed ? Object.fromEntries(next) : layout;
}
/**
 * Resolve parse layout order based on locale preference while preserving
 * existing pair-swap semantics used by Tempo.
 *
 * @remarks
 * Must return the original layout reference when no reordering or swaps occur (no-op paths).
 * This preserves referential equality for consumers expecting `resolvedLayout === originalLayout`
 * when the locale preference matches existing order or no swap pairs are found.
 */
export function resolveLayoutOrder({ layout, monthDayLayouts, isMonthDay, layoutController, classification }) {
    const ordered = resolveLayoutClassificationOrder(layout, layoutController ?? createLayoutController(layout), classification ?? DEFAULT_LAYOUT_CLASS);
    const entries = ownEntries(ordered);
    const layouts = resolveLayoutOrderPure(ordered, monthDayLayouts, isMonthDay);
    if (layouts.length !== entries.length)
        return ordered;
    const changed = layouts.some((entry, idx) => entry[0] !== entries[idx][0]);
    if (changed)
        return Object.fromEntries(layouts);
    return ordered;
}
/** return the current symbol-descriptions in parse order for debug diagnostics */
export function getLayoutOrder(layout) {
    return ownEntries(layout)
        .map(([key]) => key.description ?? String(key));
}
